// Simple tag parsing and expansion utilities
// Tag syntax: [[type:identifier]]
// Example: [[url:https://example.com/file.txt]]
// Providers map shape: { [type]: { fetch: async (id) => raw, parse?: async (raw) => string } }

/**
 * Find all tags in the given text.
 * @param {string} text
 * @returns {{full:string,type:string,id:string,index:number}[]} list of matches in order
 */
export function parseTags(text) {
  if (!text || typeof text !== 'string') return [];
  // Hyphen does not need escaping inside character class
  const re = /\[\[([a-zA-Z0-9_-]+?):([^\]]+?)\]\]/g;
  const results = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    results.push({ full: m[0], type: m[1], id: m[2], index: m.index });
  }
  return results;
}

/**
 * Expand tags in the text using the given providers. If a tag cannot be expanded, it is left as-is.
 * @param {string} text
 * @param {Record<string,{fetch:Function, parse?:Function}>} providers
 * @returns {Promise<string>} text with tags replaced
 */
export async function expandTags(text, providers = {}) {
  const tags = parseTags(text);
  if (tags.length === 0) return text;

  let out = text;
  // Replace from last to first to avoid messing up indices
  for (let i = tags.length - 1; i >= 0; i--) {
    const t = tags[i];
    const p = providers[t.type];
    if (!p || typeof p.fetch !== 'function') continue;
    try {
      const raw = await p.fetch(t.id);
      const parsed = p.parse ? await p.parse(raw, t.id) : String(raw ?? '');
      if (parsed && typeof parsed === 'string') {
        out = out.slice(0, t.index) + parsed + out.slice(t.index + t.full.length);
      }
    } catch (_) {
      // Leave the tag as-is on error
    }
  }
  return out;
}

// Example built-in providers

/**
 * URL provider: [[url:https://...]] -> fetches text content from the URL.
 */
export const UrlTagProvider = {
  fetch: async (url) => {
    const resp = await fetch(url, { method: 'GET' });
    if (!resp.ok) throw new Error('Failed to fetch URL');
    const ct = resp.headers.get('content-type') || '';
    if (ct.includes('application/json')) return JSON.stringify(await resp.json());
    return await resp.text();
  },
  parse: async (raw) => String(raw ?? '')
};

/**
 * Static map provider factory for quick demos/tests.
 * Usage: createMapProvider({ key: 'value', ... }) and use [[map:key]]
 */
export function createMapProvider(map) {
  return {
    fetch: async (id) => map[id],
    parse: async (raw) => String(raw ?? '')
  };
}
